import { IpaType } from "../phonemes/ipa";

/** for the synth encoding, all symbols that can be stand-alone or can have diacritics added to create a new symbol */
export const synthSymbols = [
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
  'A', 'B', 'C', 'D', 'E', /*FGH*/ 'I', 'J', 'K', 'L', /*M*/ 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', /*W*/ 'X', 'Y', 'Z',
  '?', '@', '&', '*', ';',
  "'", ',', '_',
];

/** for the synth encoding, postfixes that can be part of symbol, but aren't independent phonemes */
export const synthPost = [
  '+', ':', '-', '.', '^', '#', '[', '!', '"', '|', '~', '`',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
];
/** for the synth encoding, prefixes that can be part of symbol, but aren't independent phonemes */
export const synthPre = [
  '+', '/', // / used for diacritics on consonants
];
/** used to combine two symbols into a single phoneme */
export const synthCombiner = '+';

/*
note that several of the names for synth phonemes are inherited from the base set
and don't follow the proper rules that allows for consistent segmenting,
notably many of the affricates (which should use a [+]) and the ones ending with [;]
*/

/** just the IPA that has a direct match to supported ipa eSpeak phonemes */
export const ipaToSynthSymbols: Record<string, string> = {
  // diphthongs & special combos
  'ɑɹ': 'A+r',
  'ɐʉ̯': '&+u',
  'e̞ɹ': 'E+r',
  'ɛə̯': 'E+@',
  'ɜɹ': 'P',
  'ɪɹ': 'I+r',
  'oɹ': 'o+r',
  'ɯu̯': 'u.',
  'ɤɹ': 'U+r',
  'əʊ̯': '@+U',

  // second half of diphthongs (useful for FormantsChart)
  "a̯": 'a0',
  'ä̯': 'a#0',
  'ɑ̯': 'A0',
  'ɑ̝̯': 'A^0',
  'ɒ̯': 'A.0',
  "e̯": 'e0',
  'ø̯': 'e.0',
  'ø̞̯': 'e#0',
  'ɘ̯': 'e^0',
  'ɛ̯': 'E0',
  'ɜ̯': 'E^0',
  'œ̯': 'E.0',
  'i̯': 'i0',
  'ɨ̯': 'i^0',
  'ɪ̯': 'I0',
  'o̯': 'o0',
  "ɔ̯": 'O0',
  "u̯": 'u0',
  "ʉ̯": 'u^0',
  'ʊ̯': 'U0',
  'ʊ̞̯': 'U-0',
  'ɤ̯': 'U.0',
  'ʌ̯': 'V0',
  'y̯': 'y0',
  'ɝ̯': 'P0',
  'ə̯': '@0',
  'æ̯': '&0',
  'ɐ̯': '&^0',
  // short nasals
  'ã̯': 'a~0',
  'æ̯̃': '&~0',
  'ɐ̯̃': '&^~0',
  'ẽ̯': 'e~0',
  'ɛ̯̃': 'E~0',
  'ĩ̯': 'i~0',
  'ɪ̯̃': 'I~0',
  'õ̯': 'o~0',
  'ɔ̯̃': 'O~0',
  'ũ̯': 'u~0',
  'ʌ̯̃': 'V~0',
  'ə̯̃': '@~0',

  // vowels
  i: 'i',
  y: 'y',
  ɨ: 'i^',
  ʉ: 'u^',
  ɯ: 'u-',
  u̠: 'u#',
  u: 'u',
  ɪ: 'I',
  ʏ: 'Y',
  ʊ: 'U',
  ʊ̞: 'U-',
  e: 'e',
  ø: 'e.',
  ø̞: 'e#',
  ɘ: 'e^',
  ɵ: 'o#',
  ɤ̞: 'U#',
  ɤ: 'U.',
  o: 'o',
  e̞: 'e-',
  ə: '@',
  o̞: 'o-',
  o̝: 'o^',
  ɛ: 'E',
  œ: 'E.',
  ɜ: 'E^',
  ɞ: 'O^',
  ʌ: 'V',
  ɔ: 'O',
  æ: '&',
  ɐ: '&^',
  a: 'a',
  ɶ: 'a.',
  ɑ: 'A',
  ɒ: 'A.',
  // nasals
  ã: 'a~',
  æ̃: '&~',
  ɐ̃: '&^~',
  ẽ: 'e~',
  ɛ̃: 'E~',
  ĩ: 'i~',
  ɪ̃: 'I~',
  õ: 'o~',
  ɔ̃: 'O~',
  ũ: 'u~',
  ʌ̃: 'V~',
  ə̃: '@~',
  // other
  ə̆: '@-',
  ɝˑ: 'P3',
  ɝ: 'P',
  ä: 'a#',
  ɐ̞: 'a#',
  ɑ̝: 'A^',
  ɑ̈: 'A#',
  // lengths
  'ːː': '9',
  'ː': ':',
  'ˑ': '2',
  '\u0306': '0',

  // plosives missing ɢ ʡ
  p: 'p', pʰ: 'p/^', "p˭": 'p/0',
  b: 'b',
  t̪: 't[',
  d̪: 'd[',
  t: 't', tʰ: 't/^',
  d: 'd',
  ʈ: 't.',
  ɖ: 'd.',
  c: 'c',
  ɟ: 'J',
  k: 'k', kʰ: 'k/^', "k˭": 'k/0',
  ɡ: 'g', g: 'g',
  q: 'q',
  ʔ: '?',
  // nasals missing ɱ n̪ ɴ
  m: 'm',
  n: 'n',
  ɳ: 'n.',
  ɲ: 'n^',
  ŋ: 'N',
  // trills missing ʙ r̪ ʜ ʢ
  r: 'R',
  'r̝': 'R2',
  r̥: 'R#',
  ʀ: 'r"',
  // tap/flap missing ⱱ ɾ̪
  ɾ: '*',
  ᴅ: 't#',
  ɽ: 'r.',
  // affricates missing ɖʐ
  d͡z: 'd+z',
  t͡s: 't+s', t͡sʰ: 't+s/^',
  t͡ʃ: 't+S', t̠͡ʃ: 't+S',
  d͡ʒ: 'd+Z', d̠͡ʒ: 'd+Z',
  ʈ͡ʂ: 't.+s',
  t͡ɕ: 't+S;',
  d͡ʑ: 'd+Z;',
  c͡ç: 'c+C',
  d͡ɮ: 'd+L#',
  t͡ɬ: 't+l#',
  // fricatives missing ɸ ħ ɦ
  β: 'B',
  f: 'f',
  v: 'v',
  θ: 'T',
  ð: 'D',
  w: 'w',
  s: 's',
  z: 'z',
  ʃ: 'S',
  ʒ: 'Z',
  ʂ: 's.',
  ʐ: 'z.',
  ç: 'C',
  ʝ: 'J^',
  ɕ: 'S;',
  ʑ: 'Z;',
  x: 'x',
  ɣ: 'Q',
  χ: 'X',
  ʁ: 'Q"',
  ʕ: '?"',
  h: 'h',
  ɧ: 'h^',
  // lateral fricatives
  ɬ: 'l#',
  'ɮ': 'L#',
  // approximants missing ɻ
  ʍ: 'w#',
  ʋ: 'v#',
  ɹ: 'r',
  j: 'j',
  ɰ: 'w^',
  ɥ: 'y^',
  // lateral approximants missing l̪ ʟ
  l: 'l',
  ɫ: 'L',
  ɭ: 'l.',
  ʎ: 'l^',
  // clicks missing ‼
  kʘ: 'k.', ɡʘ: 'g.', ŋʘ: 'g.g~', k͡ʘ: 'k.', ɡ͡ʘ: 'g.', ŋ͡ʘ: 'g.g~', ʘ: 'k.',
  kǀ: 'k[', ɡǀ: 'g[', ŋǀ: 'g[g~', k͡ǀ: 'k[', ɡ͡ǀ: 'g[', ŋ͡ǀ: 'g[g~', ǀ: 'k[',
  kǁ: 'k#', ɡǁ: 'g#', ŋǁ: 'g#g~', k͡ǁ: 'k#', ɡ͡ǁ: 'g#', ŋ͡ǁ: 'g#g~', ǁ: 'k#',
  kǃ: 'k!', ɡǃ: 'g!', ŋǃ: 'g!g~', k͡ǃ: 'k!', ɡ͡ǃ: 'g!', ŋ͡ǃ: 'g!g~', ǃ: 'k!',
  kǂ: 'k^', ɡǂ: 'g^', ŋǂ: 'g^g~', k͡ǂ: 'k^', ɡ͡ǂ: 'g^', ŋ͡ǂ: 'g^g~', ǂ: 'k^',
  // voiced implosives missing ɓ ɗ ʄ ɠ ʛ
  // ejectives
  pʼ: 'p`', tʼ: 't`', cʼ: 'c`', kʼ: 'k`',

  // other phonemes
  l̩: 'l-',
  ɫ̩: 'L-',
  m̩: 'm-',
  n̩: 'n-',
  ŋ̩: 'N-',
  r̩: 'R-',
  'ʰ': 'h0',
  'ʲ': ';',
  '\u0329': '-',  // syllabic

  // tones
  '˨˩˦': 'K+4', // 3 tones
  '˨˦˨': 'K^',
  '˦˨˦': 'K#',
  "˨˧": "K+2", // 2 tones
  "˧˨": "K+3",
  "˧˥": "K+5",
  '˦˨': 'K6',
  '˨˦': 'K7',
  '˥˩': 'K8',
  '˩˥': 'K9',
  '˩': 'K1',  // 1 tone
  '˨': 'K2',
  '˧': 'K3',
  '˦': 'K4',
  '˥': 'K5',

  '|': '_,',
  '|ː': '_-',
  '‖': '_.',
  // placeholders, not actual synth encoding
  '?': '_?',
  '!': '_!',
};

/** map ipa espeak phonemes to closest IPA */
export const synthSymbolsToIpa: Record<string, IpaType> = {
  ' ': ' ',

  'a.': 'ɶ',
  'a#': 'ä',
  'a~': 'ã',
  'a': 'a',
  'A+r:': 'ɑːɹ',
  'A+r': 'ɑɹ',
  'A^': 'ɑ̝',
  'A#': 'ɑ̈',
  'A.': 'ɒ',
  'A': 'ɑ',
  'b': 'b',
  'B': 'β',
  'c+C': 'c͡ç',
  'c`': 'cʼ',
  'c': 'c',
  'C': 'ç',
  'd+L#': 'd͡ɮ',
  'd+z': 'd͡z',
  'd+Z;': 'd͡ʑ',
  'd+Z': 'd͡ʒ',
  'd.': 'ɖ',
  'd[': 'd̪',
  'd': 'd',
  'D': 'ð',
  'e-': 'e̞',
  'e^': 'ɘ',
  'e.': 'ø',
  'e#': 'ø̞',
  'e~': 'ẽ',
  'e': 'e',
  'E+r:': 'e̞ːɹ',
  'E+r': 'e̞ɹ',
  'E+@': 'ɛə̯',
  'E^': 'ɜ',
  'E.': 'œ',
  'E~': 'ɛ̃',
  'E': 'ɛ',
  'f': 'f',
  'g.g~': 'ŋ͡ʘ', 'g.': 'ɡ͡ʘ',
  'g[g~': 'ŋ͡ǀ', 'g[': 'ɡ͡ǀ',
  'g#g~': 'ŋ͡ǁ', 'g#': 'ɡ͡ǁ',
  'g!g~': 'ŋ͡ǃ', 'g!': 'ɡ͡ǃ',
  'g^g~': 'ŋ͡ǂ', 'g^': 'ɡ͡ǂ',
  'g': 'ɡ',
  'h0': 'ʰ',
  'h^': 'ɧ',
  'h': 'h',
  'I+r:': 'ɪːɹ',
  'I+r': 'ɪɹ',
  'i^': 'ɨ',
  'i~': 'ĩ',
  'i': 'i',
  'I~': 'ɪ̃',
  'I': 'ɪ',
  'j': 'j',
  'J^': 'ʝ',
  'J': 'ɟ',
  'k.': 'k͡ʘ',
  'k[': 'k͡ǀ',
  'k#': 'k͡ǁ',
  'k!': 'k͡ǃ',
  'k^': 'k͡ǂ',
  'k/^': 'kʰ',
  'k`': 'kʼ',
  'k/0': 'k˭',
  'k': 'k',
  'l#': 'ɬ',
  'l.': 'ɭ',
  'l^': 'ʎ',
  'l-': 'l̩',
  'l': 'l',
  'L#': 'ɮ',
  'L-': 'ɫ̩',
  'L': 'ɫ',
  'm-': 'm̩',
  'm': 'm',
  'n.': 'ɳ',
  'n^': 'ɲ',
  'n-': 'n̩',
  'n': 'n',
  'N-': 'ŋ̩',
  'N': 'ŋ',
  'o+r:': 'oːɹ',
  'o+r': 'oɹ',
  'o-': 'o̞',
  'o^': 'o̝',
  'o#': 'ɵ',
  'o~': 'õ',
  'o': 'o',
  'O+r:': 'ɑ̝ːɹ',
  'O^': 'ɞ',
  'O~': 'ɔ̃',
  'O': 'ɔ',
  'p/^': 'pʰ',
  'p`': 'pʼ',
  'p/0': 'p˭',
  'p': 'p',
  'P3': 'ɝˑ',
  'P': 'ɝ',
  'q': 'q',
  'Q"': 'ʁ',
  'Q': 'ɣ',
  'r.': 'ɽ',
  'r"': 'ʀ',
  'r': 'ɹ',
  'R-': 'r̩',
  'R2': 'r̝',
  'R#': 'r̥',
  'R': 'r',
  's.': 'ʂ',
  's': 's',
  'S;': 'ɕ',
  'S': 'ʃ',
  't+l#': 't͡ɬ',
  't+s/^': 't͡sʰ',
  't+s': 't͡s',
  't.+s': 'ʈ͡ʂ',
  't+S;': 't͡ɕ',
  't+S': 't͡ʃ',
  't.': 'ʈ',
  't[': 't̪',
  't#': 'ᴅ',
  't`': 'tʼ',
  't/^': 'tʰ',
  't': 't',
  'T': 'θ',
  'u.': 'ɯu̯',
  'u^': 'ʉ',
  'u-': 'ɯ',
  'u~': 'ũ',
  'u#': 'u̠',
  'u': 'u',
  'U+r:': 'ɤːɹ',
  'U+r': 'ɤɹ',
  'U.': 'ɤ',
  'U#': 'ɤ̞',
  'U-': 'ʊ̞',
  'U': 'ʊ',
  'v#': 'ʋ',
  'v': 'v',
  'V~': 'ʌ̃',
  'V': 'ʌ',
  'w#': 'ʍ',
  'w^': 'ɰ',
  'w': 'w',
  'x': 'x',
  'X': 'χ',
  'y^': 'ɥ',
  'y': 'y',
  'Y': 'ʏ',
  'z.': 'ʐ',
  'Z;': 'ʑ',
  'z': 'z',
  'Z': 'ʒ',

  '&+u': 'ɐʉ̯',
  '&^~': 'ɐ̃',
  '&^': 'ɐ',
  '&~': 'æ̃',
  '&': 'æ',
  '@+U': 'əʊ̯',
  '@~': 'ə̃',
  '@-': 'ə̆',
  '@': 'ə',
  '?"': 'ʕ',
  '?': 'ʔ',
  '*': 'ɾ',
  ';': 'ʲ',
  '-': '\u0329',  // syllabic

  "K+2": "˨˧",   // 23
  "K+3": "˧˨",   // 32
  "K+4": "˨˩˦",   // 214
  "K+5": "˧˥",   // 35
  'K^': '˨˦˨',
  'K#': '˦˨˦',
  'K6': '˦˨',
  'K7': '˨˦',
  'K8': '˥˩',
  'K9': '˩˥',
  'K1': '˩',
  'K2': '˨',
  'K3': '˧',
  'K4': '˦',
  'K5': '˥',

  ':': 'ː',
  // custom vowel lengths
  0: '\u0306', 1: '\u0306',
  2: 'ˑ', 3: 'ˑ',
  4: '', 5: '',
  6: 'ː', 7: 'ː',
  8: 'ːː', 9: 'ːː',

  '_,': '|',  // minor-break, comma
  '_-': '|ː', // medium break, semi-colon
  '_.': '‖',  // major-break, period
  // placeholders, not actual synth encoding
  '_?': '?',
  '_!': '!',

  "'!": 'ˈˈ',
  "'": 'ˈ',
  ',': 'ˌ',
};

export const shortBreak = '_,';
export const mediumBreak = '_-';

/** tones supported by the synth encoding */
export enum SynthTones {
  ExtraLow = '˩',  // 1
  Low = '˨',       // 2
  Medium = '˧',    // 3
  High = '˦',      // 4
  ExtraHigh = '˥', // 5
  FallSmall = '˦˨', // 42
  RiseSmall = '˨˦', // 24
  FallBig = '˥˩',   // 51
  RiseBig = '˩˥',   // 15
  Mountain = '˨˦˨',  // 242
  Valley = '˦˨˦',    // 424
  T23 = '˨˧',       // 23
  T32 = '˧˨',       // 32
  T35 = '˧˥',       // 35
  T214 = '˨˩˦',      // 214
}

/** find the closest match to the desired length as described in the file ph_ipa */
export function millisecondsToSynth(ms: number): string {
  if (ms < 63) return "0";   // 50
  if (ms < 88) return "1";   // 75
  if (ms < 115) return "2";  // 100
  if (ms < 145) return "3";  // 130
  if (ms < 175) return "4";  // 160
  if (ms < 205) return "5";  // 190
  if (ms < 240) return "6";  // 220
  if (ms < 280) return "7";  // 260
  if (ms < 320) return "8";  // 300
  if (ms < 360) return ":6"; // 340
  if (ms < 400) return ":7"; // 380
  if (ms < 440) return ":8"; // 420
  if (ms < 520) return "9";  // 460
  return ":9";  // 580 = 120 + 460
}
export function millisecondsToIpa(ms: number): string {
  const synth = millisecondsToSynth(ms);
  return synth[0] === ":" ? `ː${synth[1]}` : synth;
}
export const symbolToMilliseconds = [50, 75, 100, 130, 160, 190, 220, 260, 300, 460];
export const longToMilliseconds = { ':6': '340', ':7': '380', ':8': '420', ':9': '580' };

export const shortestInMilliseconds = 50;
/** adding a length 0 vowel to a longer vowel typically adds roughly 10ms */
export const plusExtraShort = 10;

export const fixedPhonemeLengths: Record<string, number> = {
  // approximates
  'B': 70,  // β̞
  'v#': 70, // ʋ
  'w': 90,  // w
  'D': 60,  // ð̞
  'r': 45,  // ɹ ɻ
  'j': 90,  // j
  'Q': 65,  // ɰ
  'Q"': 65, // ʁ̞
  // lateral approximates
  'l': 100,  // l l̪ l̥ ʟ
  'l.': 85, // ɭ
  'l;': 85, // ȴ
  'l^': 85, // ʎ
  // some taps & trills
  '*': 50,  // ɾ
  'r.': 50, // ɽ
  'R': 50,  // r
}
